import { CountryCodeType } from 'models/countries';
import { GroupByAttribute } from 'services/data.service';
import { trackMatomoEvent } from 'services/matomo.service';
import { StateCreator } from 'zustand/vanilla';
import {
  defaultDateRange,
  MONTHLY_PRESET_REGISTRY,
  MONTHLY_COMPARISON_REGISTRY,
} from 'components/filter/input/DateRangePicker/presets';
import { defaultCreationDateRange } from 'components/filter/modules/article-visibility/defaults';
import {
  BENCHMARKING_TEMPORAL_TYPE_MONTHLY,
  BenchmarkingDateValue,
} from 'components/filter/modules/benchmarking/types';
import type { DateValue } from 'components/filter/input/DateRangePicker';
import { areUnorderedArrayValuesEqual } from 'utils/arrayUtils';
import { getComparisonKey, getRangeKey } from 'utils/dateUtils';

const MATOMO_CATEGORY = 'Filters';

const trackMultiSelectFilter = (
  filterName: string,
  previous: readonly string[] | undefined,
  next: string[] | undefined
) => {
  if (areUnorderedArrayValuesEqual(previous, next)) return;
  if (next?.length) {
    trackMatomoEvent({ category: MATOMO_CATEGORY, action: filterName, name: 'Set', value: next.length });
    return;
  }
  trackMatomoEvent({ category: MATOMO_CATEGORY, action: filterName, name: 'Clear' });
};

export interface FilterData {
  dateRange: DateValue;
  creationDateRange: DateValue;
  benchmarkingDateRange: BenchmarkingDateValue;
  dateBy?: string;
  brands?: string[];
  countries?: CountryCodeType[];
  genders?: string[];
  ageGroups?: string[];
  seasons?: string[];
  categories?: string[];
  benchmark: string;
  marketplaces?: string[];
  products?: string[];
  fulfillmentMethods?: string[];
  colors?: string[];
  sizes?: string[];
  groupByAttribute?: GroupByAttribute;
  benchmarkingPerformanceSection: 'yoy' | 'mom';
}

export type AdvancedFilterValues = Pick<
  FilterData,
  | 'dateRange'
  | 'creationDateRange'
  | 'brands'
  | 'countries'
  | 'genders'
  | 'ageGroups'
  | 'seasons'
  | 'categories'
  | 'benchmark'
  | 'marketplaces'
  | 'products'
  | 'fulfillmentMethods'
  | 'colors'
  | 'sizes'
>;

export interface FilterActions {
  setAdvancedFilters: (filters: AdvancedFilterValues) => void;
  setDateRange: (dateRange: DateValue) => void;
  setCreationDateRange: (dateRange: DateValue) => void;
  setBenchmarkingDateRange: (dateRange: BenchmarkingDateValue) => void;
  setDateBy: (dateBy: string) => void;
  setBrands: (brands: string[]) => void;
  setCountries: (countries: CountryCodeType[]) => void;
  setGenders: (genders: string[]) => void;
  setAgeGroups: (ageGroups: string[]) => void;
  setSeasons: (seasons: string[]) => void;
  setCategories: (categories: string[]) => void;
  setBenchmark: (benchmark: string) => void;
  setMarketplaces: (marketplaces: string[]) => void;
  setProducts: (products: string[]) => void;
  setFulfillmentMethods: (fulfillmentMethods: string[]) => void;
  setColors: (colors: string[]) => void;
  setSizes: (sizes: string[]) => void;
  setGroupByAttribute: (groupByAttribute: GroupByAttribute) => void;
  setBenchmarkingPerformanceSection: (section: 'yoy' | 'mom') => void;
  clearAll: () => void;
}

export interface FilterSlice extends FilterData, FilterActions {}

const getDefaultBenchmarkingDateRange = (): BenchmarkingDateValue => {
  const today = new Date();
  const mainRange = MONTHLY_PRESET_REGISTRY.last_3_months.calculate(today)!;
  const comparisonRange = MONTHLY_COMPARISON_REGISTRY.previous_period.calculate(today, mainRange)!;
  return {
    temporalType: BENCHMARKING_TEMPORAL_TYPE_MONTHLY,
    range: { presetValue: 'last_3_months', ...mainRange },
    comparison: { presetValue: 'previous_period', ...comparisonRange },
  };
};

const emptyFilters = {
  creationDateRange: defaultCreationDateRange(),
  benchmarkingDateRange: getDefaultBenchmarkingDateRange(),
  dateBy: 'order',
  benchmark: 'all',
  countries: undefined,
  categories: undefined,
  products: undefined,
  genders: undefined,
  ageGroups: undefined,
  seasons: undefined,
  marketplaces: undefined,
  brands: undefined,
  fulfillmentMethods: undefined,
  colors: undefined,
  sizes: undefined,
  groupByAttribute: null,
  benchmarkingPerformanceSection: 'yoy',
  dateRange: defaultDateRange(),
} as FilterData;

export const createFilterSlice: StateCreator<FilterSlice, [], [], FilterSlice> = set => ({
  ...emptyFilters,
  setAdvancedFilters: filters =>
    set(state => {
      trackMultiSelectFilter('Brands', state.brands, filters.brands);
      trackMultiSelectFilter('Countries', state.countries, filters.countries);
      trackMultiSelectFilter('Marketplaces', state.marketplaces, filters.marketplaces);
      trackMultiSelectFilter('Categories', state.categories, filters.categories);
      trackMultiSelectFilter('Fulfillment', state.fulfillmentMethods, filters.fulfillmentMethods);
      trackMultiSelectFilter('Gender', state.genders, filters.genders);
      trackMultiSelectFilter('Age Groups', state.ageGroups, filters.ageGroups);
      trackMultiSelectFilter('Season', state.seasons, filters.seasons);
      trackMultiSelectFilter('Colors', state.colors, filters.colors);
      trackMultiSelectFilter('Sizes', state.sizes, filters.sizes);

      if (getRangeKey(state.dateRange) !== getRangeKey(filters.dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Time Frame',
          name: filters.dateRange.range?.presetValue ?? 'custom',
        });
      }
      if (getComparisonKey(state.dateRange) !== getComparisonKey(filters.dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Comparison Time Frame',
          name: filters.dateRange.comparison?.presetValue ?? 'no_comparison',
        });
      }
      if (getRangeKey(state.creationDateRange) !== getRangeKey(filters.creationDateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Creation Dates',
          name: filters.creationDateRange.range?.presetValue ?? 'custom',
        });
      }
      if (state.benchmark !== filters.benchmark) {
        trackMatomoEvent({ category: MATOMO_CATEGORY, action: 'Peer Group', name: filters.benchmark });
      }

      return {
        dateRange: filters.dateRange,
        creationDateRange: filters.creationDateRange,
        brands: filters.brands?.length ? filters.brands : undefined,
        countries: filters.countries?.length ? filters.countries : undefined,
        genders: filters.genders?.length ? filters.genders : undefined,
        ageGroups: filters.ageGroups?.length ? filters.ageGroups : undefined,
        seasons: filters.seasons?.length ? filters.seasons : undefined,
        categories: filters.categories?.length ? filters.categories : undefined,
        benchmark: filters.benchmark,
        marketplaces: filters.marketplaces?.length ? filters.marketplaces : undefined,
        products: filters.products?.length ? filters.products : undefined,
        fulfillmentMethods: filters.fulfillmentMethods?.length ? filters.fulfillmentMethods : undefined,
        colors: filters.colors?.length ? filters.colors : undefined,
        sizes: filters.sizes?.length ? filters.sizes : undefined,
      };
    }),
  setGroupByAttribute: groupByAttribute => set(() => ({ groupByAttribute })),
  setBenchmarkingPerformanceSection: section => set(() => ({ benchmarkingPerformanceSection: section })),
  clearAll: () =>
    set(() => {
      return emptyFilters;
    }),
  setBrands: brands =>
    set(state => {
      const next = brands.length > 0 ? brands : undefined;
      trackMultiSelectFilter('Brands', state.brands, next);
      return { brands: next };
    }),
  setCountries: countries =>
    set(state => {
      const next = countries.length > 0 ? countries : undefined;
      trackMultiSelectFilter('Countries', state.countries, next);
      return { countries: next };
    }),
  setMarketplaces: marketplaces =>
    set(state => {
      const next = marketplaces.length > 0 ? marketplaces : undefined;
      trackMultiSelectFilter('Marketplaces', state.marketplaces, next);
      return { marketplaces: next };
    }),
  setCategories: categories =>
    set(state => {
      const next = categories.length > 0 ? categories : undefined;
      trackMultiSelectFilter('Categories', state.categories, next);
      return { categories: next };
    }),
  setDateRange: dateRange =>
    set(state => {
      if (getRangeKey(state.dateRange) !== getRangeKey(dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Time Frame',
          name: dateRange.range?.presetValue ?? 'custom',
        });
      }
      if (getComparisonKey(state.dateRange) !== getComparisonKey(dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Comparison Time Frame',
          name: dateRange.comparison?.presetValue ?? 'no_comparison',
        });
      }
      return { dateRange };
    }),
  setCreationDateRange: dateRange =>
    set(state => {
      if (getRangeKey(state.benchmarkingDateRange) !== getRangeKey(dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Creation Dates',
          name: dateRange.range?.presetValue ?? 'custom',
        });
      }
      return { creationDateRange: dateRange };
    }),
  setBenchmarkingDateRange: dateRange =>
    set(state => {
      if (getRangeKey(state.benchmarkingDateRange) !== getRangeKey(dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Time Frame',
          name: dateRange.range?.presetValue ?? 'custom',
        });
      }
      if (getComparisonKey(state.benchmarkingDateRange) !== getComparisonKey(dateRange)) {
        trackMatomoEvent({
          category: MATOMO_CATEGORY,
          action: 'Comparison Time Frame',
          name: dateRange.comparison?.presetValue ?? 'no_comparison',
        });
      }
      return { benchmarkingDateRange: dateRange };
    }),
  setBenchmark: benchmark =>
    set(state => {
      if (state.benchmark !== benchmark) {
        trackMatomoEvent({ category: MATOMO_CATEGORY, action: 'Peer Group', name: benchmark });
      }
      return { benchmark };
    }),
  setDateBy: dateBy => set(() => ({ dateBy })),
  setFulfillmentMethods: fulfillmentMethods =>
    set(state => {
      const next = fulfillmentMethods.length > 0 ? fulfillmentMethods : undefined;
      trackMultiSelectFilter('Fulfillment', state.fulfillmentMethods, next);
      return { fulfillmentMethods: next };
    }),
  setGenders: genders =>
    set(state => {
      const next = genders.length > 0 ? genders : undefined;
      trackMultiSelectFilter('Gender', state.genders, next);
      return { genders: next };
    }),
  setAgeGroups: ageGroups =>
    set(state => {
      const next = ageGroups.length > 0 ? ageGroups : undefined;
      trackMultiSelectFilter('Age Groups', state.ageGroups, next);
      return { ageGroups: next };
    }),
  setSeasons: seasons =>
    set(state => {
      const next = seasons.length > 0 ? seasons : undefined;
      trackMultiSelectFilter('Season', state.seasons, next);
      return { seasons: next };
    }),
  setProducts: products => set(() => ({ products: products.length > 0 ? products : undefined })),
  setColors: colors =>
    set(state => {
      const next = colors.length > 0 ? colors : undefined;
      trackMultiSelectFilter('Colors', state.colors, next);
      return { colors: next };
    }),
  setSizes: sizes =>
    set(state => {
      const next = sizes.length > 0 ? sizes : undefined;
      trackMultiSelectFilter('Sizes', state.sizes, next);
      return { sizes: next };
    }),
});
